/** ITCSI support access (M14-07): ITCSI asks, the owner decides, every page is logged. */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { dateTime } from '../../lib/format';
import { Alert, Button, Card, EmptyState, PageHeader, Skeleton, StatusChip, filterClass } from '../../components/ui';
const errorText = (e: unknown): string => (e instanceof Error ? e.message : 'Something went wrong.');

interface Session { id: string; reason: string; hours: number; status: 'PENDING' | 'ACTIVE' | 'ENDED' | 'DECLINED'; requestedBy: string; startsAt: string | null; endsAt: string | null; requests: number; createdAt: string }
const SESSION_TONE = { PENDING: 'warning', ACTIVE: 'info', ENDED: 'neutral', DECLINED: 'neutral' } as const;

export function SupportAccessPage(): JSX.Element {
  const { can } = useAuth();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['support-sessions'], queryFn: () => api<Session[]>('/company/support-sessions'), refetchInterval: 30_000 });
  const [hours, setHours] = useState<Record<string, number>>({});
  const act = useMutation({
    mutationFn: ({ id, action, body }: { id: string; action: 'approve' | 'decline' | 'end'; body?: unknown }) => api(`/company/support-sessions/${id}/${action}`, { method: 'POST', body: body ?? {} }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['support-sessions'] }),
  });
  const manage = can('users.manage');
  return (
    <>
      <PageHeader title="ITCSI Support Access" description="ITCSI cannot see your books. When you ask for help, approve a read-only session for a few hours; it ends by itself, and every page support opens is logged." />
      {act.error && <div className="mb-4"><Alert tone="critical">{errorText(act.error)}</Alert></div>}
      {list.isLoading && <Skeleton className="h-32 w-full" />}
      {list.data?.length === 0 && <Card><EmptyState title="No support requests" description="Requests from ITCSI support appear here for you to approve or decline." /></Card>}
      <div className="space-y-3">
        {list.data?.map((s) => (
          <Card
            key={s.id}
            title={`${s.requestedBy} · ${s.status === 'PENDING' ? `asks for ${s.hours} hour${s.hours === 1 ? '' : 's'}` : s.status.toLowerCase()}`}
            description={`Requested ${dateTime(s.createdAt)}${s.endsAt ? ` · ${s.status === 'ACTIVE' ? 'ends' : 'ended'} ${dateTime(s.endsAt)}` : ''}${s.requests ? ` · ${s.requests} pages opened` : ''}`}
            actions={
              <div className="flex flex-wrap items-center gap-2">
                <StatusChip tone={SESSION_TONE[s.status]}>{s.status.toLowerCase()}</StatusChip>
                {manage && s.status === 'PENDING' && (
                  <>
                    <select className={filterClass} value={hours[s.id] ?? s.hours} onChange={(e) => setHours({ ...hours, [s.id]: Number(e.target.value) })} aria-label="Hours to allow">
                      {[1, 2, 4, 8, 24].map((h) => <option key={h} value={h}>{h} h</option>)}
                    </select>
                    <Button size="sm" busy={act.isPending} onClick={() => act.mutate({ id: s.id, action: 'approve', body: { hours: hours[s.id] ?? s.hours } })}>Approve read-only</Button>
                    <Button size="sm" variant="secondary" onClick={() => act.mutate({ id: s.id, action: 'decline' })}>Decline</Button>
                  </>
                )}
                {manage && s.status === 'ACTIVE' && <Button size="sm" variant="danger" onClick={() => act.mutate({ id: s.id, action: 'end' })}>End now</Button>}
              </div>
            }
          >
            <p className="text-sm text-slate-700 dark:text-slate-200">“{s.reason}”</p>
          </Card>
        ))}
      </div>
    </>
  );
}
